import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { camelCaseKeys, snakeCaseKeys } from '../util/object';
import { deleteRequestJSON,getRequestJSON, postRequestJSON, putRequestJSON } from '../util/services';
import { useConfig } from './useConfig';

// Label Type interfaces
export interface LabelType {
  id: number;
  labelType: string;
  createdAt: string;
  updatedAt: string;
}

// Label Entry interfaces
export interface LabelEntry {
  id: number;
  labelTypeId: number;
  entry: string;
  createdAt: string;
  updatedAt: string;
  labelTypeName: string;
}

// Hook to fetch all label types
export function useLabelTypes() {
  const { apiHost } = useConfig();

  return useQuery({
    queryKey: ['labelTypes'],
    queryFn: () => getRequestJSON(apiHost, '/admin/label_types'),
    select: (data) => camelCaseKeys(data)
  });
}

// Hook to create a new label type
export function useCreateLabelType() {
  const { apiHost } = useConfig();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (labelType: { labelType: string }) => 
      postRequestJSON(apiHost, '/admin/label_types', snakeCaseKeys({ labelType })),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['labelTypes'] });
    }
  });
}

// Hook to update a label type
export function useUpdateLabelType() {
  const { apiHost } = useConfig();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, labelType }: { id: number, labelType: string }) => 
      putRequestJSON(apiHost, `/admin/label_types/${id}`, snakeCaseKeys({ labelType: { labelType } })),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['labelTypes'] });
    }
  });
}

// Hook to delete a label type
export function useDeleteLabelType() {
  const { apiHost } = useConfig();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => 
      deleteRequestJSON(apiHost, `/admin/label_types/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['labelTypes'] });
    }
  });
}

// Hook to fetch all label entries
export function useLabelEntries() {
  const { apiHost } = useConfig();

  return useQuery({
    queryKey: ['labelEntries'],
    queryFn: () => getRequestJSON(apiHost, '/admin/label_entries'),
    select: (data) => camelCaseKeys(data)
  });
}

// Hook to create a new label entry
export function useCreateLabelEntry() {
  const { apiHost } = useConfig();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (labelEntry: { labelTypeId: number, entry: string }) => 
      postRequestJSON(apiHost, '/admin/label_entries', snakeCaseKeys({ labelEntry })),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['labelEntries'] });
    }
  });
}

// Hook to update a label entry
export function useUpdateLabelEntry() {
  const { apiHost } = useConfig();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, labelTypeId, entry }: { id: number, labelTypeId: number, entry: string }) => 
      putRequestJSON(apiHost, `/admin/label_entries/${id}`, snakeCaseKeys({ labelEntry: { labelTypeId, entry } })),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['labelEntries'] });
    }
  });
}

// Hook to delete a label entry
export function useDeleteLabelEntry() {
  const { apiHost } = useConfig();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => 
      deleteRequestJSON(apiHost, `/admin/label_entries/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['labelEntries'] });
    }
  });
}
